import React, { useState, useEffect, useRef } from 'react';
import * as classes from './OverdraftCalculator.module.scss';
import Tooltip from '../tooltip/Tooltip';
import Input from '../input/Input';
import Select from '../select/Select';

import {
  BORROW_TERM_DAYS,
  NON_NUMERIC,
  INITIAL_RATES,
  isValidBorrowAmount,
  debounce,
  cleanBorrowAmount,
  setRangeSliderCSSVariables
} from './utilities';
import useUnmounted from '../../hooks/useUnmounted';
import { convertNumberToCurrency } from '../../../src/utils.js';
import { slugify } from '../../utils';
import RadioInput from '../radioInput/RadioInput.js';

async function getRatesFromDatabase(borrowAmount, borrowTerm) {
  const calculationUrl =
    typeof window !== 'undefined' && window.starlingbank
      ? window.starlingbank.getConfig('overdraftCalculator')
      : 'https://grykowtbdcd6ngwjfpmvkx2vf40ewhif.lambda-url.eu-west-2.on.aws/';
  const queryParams = `?do=getStarling2020Rates&amount=${borrowAmount}&days=${borrowTerm}`;

  // Make call to endpoint and return data

  if (0 < borrowAmount && borrowAmount <= 5000) {
    try {
      const response = await fetch(calculationUrl + queryParams);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return (index) => {
        return {
          rawRate: `${Math.round(data[index].rawRate * 10) / 10}%`,
          cost: convertNumberToCurrency(data[index].rawCost, 'en-GB', 'GBP'),
          label: data[index].product.label
        };
      };
    } catch (error) {
      console.error('Error fetching rates:', error);
      throw error;
    }
  }
}

const getRatesFromDatabaseDebounced = debounce(getRatesFromDatabase, 500);

const OverdraftCalculator = () => {
  const unmounted = useUnmounted(); // returns ref
  const rangeSlider = useRef(null);
  const [borrowAmount, _setBorrowAmount] = useState(500);
  const [borrowTerm, setBorrowTerm] = useState(BORROW_TERM_DAYS[1]);
  const [rates, setRates] = useState(INITIAL_RATES);
  const [isDragging, setIsDragging] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [inputError, setInputError] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [overdraftType, setOverdraftType] = useState('arranged');

  const setBorrowAmount = (inputValue) => {
    if (typeof inputValue === 'string') {
      // Show error if non numeric chars present
      if (inputValue.search(NON_NUMERIC) !== -1) {
        setInputError(true);
      }

      // Clean string, convert to Number and update borrowAmount
      _setBorrowAmount(cleanBorrowAmount(inputValue, NON_NUMERIC));
    }
  };

  useEffect(() => {
    setRangeSliderCSSVariables(rangeSlider.current, borrowAmount);
    // Toggle results table visibility
    setShowResults(isValidBorrowAmount(borrowAmount));
  }, [borrowAmount]);

  useEffect(() => {
    // Reset request error
    setRequestError(false);

    // Retrieve our rates and display in results table
    if (!isDragging && isValidBorrowAmount(borrowAmount)) {
      setIsFetching(true);

      getRatesFromDatabaseDebounced(borrowAmount, borrowTerm)
        .then((rates) => {
          if (!unmounted.current) {
            setRates([rates(0), rates(1), rates(2)]);
            setIsFetching(false);
          }
        })
        .catch((error) => {
          console.log(error);
          if (!unmounted.current) {
            setRequestError(true);
            setShowResults(false);
          }
        });
    }
  }, [borrowAmount, borrowTerm, isDragging, unmounted]);

  return (
    <div className={classes.overdraftCalculator}>
      <div className={classes.overdraftCalculator_wrapper}>
        <header className={inputError ? classes.overdraftCalculator_error : undefined}>
          <h2 id="potential cost" className={classes.overdraftCalculator_title}>
            See how much a Starling overdraft could cost you from 1st April 2020
          </h2>

          <div className={classes.rangeSlider}>
            <span>£1</span>
            <input
              ref={rangeSlider}
              type="range"
              min="1"
              max="5000"
              value={borrowAmount}
              onChange={(event) => setBorrowAmount(event.target.value)}
              onTouchEnd={() => setIsDragging(false)}
              onTouchStart={() => setIsDragging(true)}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              onInput={() => setInputError(false)}
              aria-labelledby="potential-cost"
            />
            <span>£5,000</span>
          </div>
          <div className={classes.borrowDetails}>
            <p id="borrowing">Borrowing £</p>
            <div className={classes.borrowDetails_amount}>
              <Input
                id="borrowAmountInput"
                min={0}
                max={5000}
                value={String(borrowAmount)}
                onChange={(event) => setBorrowAmount(event.target.value)}
                onInput={() => setInputError(false)}
                variant={'overdraftCalculator'}
                aria-labelledby="borrowing"
              />
            </div>
            <p>for</p>
            <div className={classes.borrowDetails_duration}>
              <Select
                value={borrowTerm}
                defaultValue={borrowTerm}
                options={{ 7: '7', 30: '30', 60: '60', 90: '90' }}
                onChange={setBorrowTerm}
                variant={'overdraftCalculator'}
                aria-labelledby="days"
              />
            </div>{' '}
            <p id="days">days will cost you</p>
          </div>
          {inputError && <p className={classes.overdraftCalculator_error}>Please only enter numeric values</p>}

          {!isValidBorrowAmount(borrowAmount) && (
            <p className={classes.overdraftCalculator_error}>Please enter an amount between £1 and £5,000</p>
          )}

          <div className={classes.overdraftCalculator_radio_container}>
            <RadioInput
              label="Arranged"
              name="overdraft-type"
              id="arranged"
              defaultChecked
              onChange={() => setOverdraftType('arranged')}
            />
            <RadioInput
              label="Unarranged"
              name="overdraft-type"
              id="unarranged"
              onChange={() => setOverdraftType('unarranged')}
            />
          </div>
        </header>

        {showResults && (
          <table className={classes.overdraftCalculator_table_data}>
            <tbody>
              <tr>
                <th>&nbsp;</th>
                <th scope="col">Cost</th>
                <th scope="col" colSpan="2">
                  Rate (% APR variable)
                </th>
              </tr>
              {overdraftType === 'arranged' ? (
                rates.map((rate) => {
                  return (
                    <tr key={rate.label}>
                      <th scope="row">{rate.label} </th>
                      <td>{isDragging || isFetching ? '-' : rate.cost === '£NaN' ? '£0' : rate.cost}</td>
                      <td>{isDragging || isFetching ? '-' : rate.rawRate}</td>

                      <td>
                        <Tooltip
                          id={slugify(rate.label)}
                          list={[
                            `${rate.label
                              .replace('Starling current account (', '')
                              .replace(')', '')} EAR variable with no fees or usage charges.`,
                            'Based on interest charge being applied on 15th of subsequent month.'
                          ]}
                          customText="i"
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <th scope="row">
                    We don't offer unarranged overdrafts and will look to stop payments which would take you into
                    unarranged borrowing. However, if your account does go into an unarranged overdraft we will not
                    charge for this.
                  </th>
                  <td>£0</td>
                  <td>0%</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {requestError && borrowAmount !== 0 && (
          <p className={classes.overdraftCalculator_error}>
            There has been an error retrieving results, please try again.
          </p>
        )}
        <footer>
          <h4>It’s worth noting</h4>
          <p>
            In scenarios that are greater than one calendar month (60 days and 90 days), it is assumed that all interest
            and other charges are repaid by the borrower in full at the time they are charged. These are not capitalised
            and therefore, do not increase the interest incurred in a subsequent month.
          </p>
          <p className="screen-reader-text">More information about</p>
        </footer>
      </div>
    </div>
  );
};

export default OverdraftCalculator;
